import { useState } from 'react';
import {
    useConnectItero,
    useDisconnectItero,
    useIteroStatus,
    useRefreshItero,
    useTestIteroConnection
} from '../../../../hooks/useIteroConnection';
import { useToast } from '../../../../providers/ToastProvider';
import { iteroErrorMessage } from '../../../../services/iteroConnection';
import { formatDateTime } from '../../../../utils/dates';
import {
    iteroBoxActions,
    shouldLoadIteroStatus,
    type IteroBoxAccess,
    type IteroBoxAction
} from './iteroBoxActions';
import {
    DISCONNECT_UNKNOWN_STATUS_MESSAGE,
    connectedToast,
    disconnectConfirmMessage,
    refreshToastMessage
} from './iteroConnectionCopy';
import { useIteroBrowseLauncher } from './useIteroBrowseLauncher';

export interface IteroBoxRequests {
    status: ReturnType<typeof useIteroStatus>;
    test: ReturnType<typeof useTestIteroConnection>;
    refresh: ReturnType<typeof useRefreshItero>;
    connect: ReturnType<typeof useConnectItero>;
    disconnect: ReturnType<typeof useDisconnectItero>;
}

export interface IteroConnectionBoxState {
    requests: IteroBoxRequests;
    isBusy: boolean;
    clinicId: number | null;
    actions: IteroBoxAction[];
    pending: IteroBoxAction[];
    runAction: (action: IteroBoxAction) => void;
    isConnectOpen: boolean;
    closeConnect: () => void;
    connectClinic: (clinicId: number) => void;
    isBrowseOpen: boolean;
    closeBrowse: () => void;
    disconnectMessage: string | null;
    cancelDisconnect: () => void;
    confirmDisconnect: () => void;
}

function useIteroBoxRequests(practiceId: string, access: IteroBoxAccess): IteroBoxRequests {
    return {
        status: useIteroStatus(practiceId, shouldLoadIteroStatus(access)),
        test: useTestIteroConnection(practiceId),
        refresh: useRefreshItero(practiceId),
        connect: useConnectItero(practiceId),
        disconnect: useDisconnectItero(practiceId)
    };
}

function isRequestBusy({ status, test, refresh, connect, disconnect }: IteroBoxRequests): boolean {
    return (
        status.isFetching ||
        test.isPending ||
        refresh.isPending ||
        connect.isPending ||
        disconnect.isPending
    );
}

function pendingActions({ test, refresh }: IteroBoxRequests): IteroBoxAction[] {
    return [
        ...(test.isPending ? ['test' as const] : []),
        ...(refresh.isPending ? ['refresh' as const] : [])
    ];
}

function useIteroBoxMutations(
    { test, refresh, connect, disconnect }: IteroBoxRequests,
    closeConnect: () => void,
    closeDisconnect: () => void
) {
    const { showToast } = useToast();
    return {
        connectClinic: (nextClinicId: number) =>
            connect.mutate(nextClinicId, {
                onSuccess: (next) => {
                    showToast(connectedToast(next.clinicId ?? nextClinicId), 'success');
                    closeConnect();
                    test.reset();
                }
            }),
        refreshToken: () =>
            refresh.mutate(undefined, {
                onSuccess: (result) =>
                    showToast(refreshToastMessage(result, formatDateTime), 'success'),
                onError: (error) =>
                    showToast(iteroErrorMessage(error, "Couldn't refresh the iTero token"), 'error')
            }),
        confirmDisconnect: () =>
            disconnect.mutate(undefined, {
                onSuccess: () => {
                    showToast('Disconnected from iTero', 'success');
                    closeDisconnect();
                    test.reset();
                },
                onError: (error) =>
                    showToast(iteroErrorMessage(error, "Couldn't disconnect from iTero"), 'error')
            })
    };
}

export function useIteroConnectionBox(
    practiceId: string,
    access: IteroBoxAccess
): IteroConnectionBoxState {
    const [isConnectOpen, setIsConnectOpen] = useState(false);
    // Captured on open so the copy can't change as the status refetches before the modal closes.
    const [disconnectMessage, setDisconnectMessage] = useState<string | null>(null);
    const requests = useIteroBoxRequests(practiceId, access);
    const closeConnect = () => setIsConnectOpen(false);
    const cancelDisconnect = () => setDisconnectMessage(null);
    const mutations = useIteroBoxMutations(requests, closeConnect, cancelDisconnect);
    const { status, test, connect } = requests;
    const clinicId = status.data?.clinicId ?? null;
    const actions = iteroBoxActions(access, {
        connected: status.data?.connected ?? null,
        clinicId
    });
    const launcher = useIteroBrowseLauncher({
        canView: access.canView,
        isStatusKnown: status.data !== undefined,
        canBrowse: actions.includes('browse')
    });

    const openConnect = () => {
        connect.reset();
        setIsConnectOpen(true);
    };
    const actionHandlers: Record<IteroBoxAction, () => void> = {
        browse: launcher.open,
        test: () => test.mutate(),
        connect: openConnect,
        chooseClinic: openConnect,
        changeClinic: openConnect,
        refresh: mutations.refreshToken,
        disconnect: () =>
            setDisconnectMessage(
                status.data
                    ? disconnectConfirmMessage(status.data)
                    : DISCONNECT_UNKNOWN_STATUS_MESSAGE
            )
    };

    return {
        requests,
        isBusy: isRequestBusy(requests),
        clinicId,
        actions,
        pending: pendingActions(requests),
        runAction: (action) => actionHandlers[action](),
        isConnectOpen,
        closeConnect,
        connectClinic: mutations.connectClinic,
        isBrowseOpen: launcher.isOpen,
        closeBrowse: launcher.close,
        disconnectMessage,
        cancelDisconnect,
        confirmDisconnect: mutations.confirmDisconnect
    };
}
